import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { posts, postSrcSet } from "@/lib/blog-posts";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { breadcrumbScript, defaultOgImageMeta } from "@/lib/seo";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Textile Recycling & Secure Disposal Blog | Preserve Earth" },
      {
        name: "description",
        content:
          "Guidance on confidential waste and data protection, uniform and PPE disposal, textile recycling, EPR and secure brand destruction for UK businesses.",
      },
      {
        property: "og:title",
        content: "Textile Recycling & Secure Disposal Blog | Preserve Earth",
      },
      {
        property: "og:description",
        content:
          "Guidance on confidential waste and data protection, uniform and PPE disposal, textile recycling, EPR and secure brand destruction for UK businesses.",
      },
      { name: "twitter:title", content: "Textile Recycling & Secure Disposal Blog | Preserve Earth" },
      { name: "twitter:description", content: "Guidance on confidential waste and data protection, uniform and PPE disposal, textile recycling, EPR and secure brand destruction for UK businesses." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://preserveearth.co.uk/blog" },
      ...defaultOgImageMeta,
    ],
    links: [{ rel: "canonical", href: "https://preserveearth.co.uk/blog" }],
    scripts: [breadcrumbScript([{ name: "Blog", path: "/blog" }])],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const [feature, ...rest] = posts;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader
        links={[
          { label: "Home", href: "/" },
          { label: "About", href: "/about" },
          { label: "Services", href: "/services" },
          { label: "Blog", href: "/blog" },
        ]}
        enquireHref="/#enquire"
      />

      <main>
        <section className="container-page pt-20 pb-16">
          <Breadcrumbs crumbs={[{ name: "Blog", path: "/blog" }]} />
          <p className="mb-6 text-xs font-medium uppercase tracking-[0.25em] text-moss">
            The Ledger: Journal
          </p>
          <h1 className="max-w-[24ch] text-balance font-serif text-5xl leading-[0.95] text-charcoal lg:text-7xl">
            Textile Recycling &amp; Secure Disposal Insights
          </h1>
          <p className="mt-8 max-w-[60ch] text-pretty text-lg leading-relaxed text-charcoal/70">
            Plain-English guidance on confidential waste and data protection,
            uniform and PPE disposal, textile recycling, EPR, secure brand
            destruction and the changing compliance landscape for UK businesses.
          </p>
        </section>

        <section className="container-page pb-24">
          <Link
            to="/blog/$slug"
            params={{ slug: feature.slug }}
            className="group grid items-center gap-10 border-y border-charcoal/10 py-10 lg:grid-cols-12 lg:gap-16"
          >
            <figure className="overflow-hidden rounded-md outline outline-1 -outline-offset-1 outline-charcoal/10 lg:col-span-7">
              <img
                src={feature.image}
                srcSet={postSrcSet(feature)}
                sizes="(min-width: 1024px) 58vw, 100vw"
                alt={feature.title}
                width={1440}
                height={756}
                loading="eager"
                fetchPriority="high"
                className="aspect-[16/9] w-full object-cover transition-transform group-hover:scale-[1.02]"
              />
            </figure>
            <div className="lg:col-span-5">
              <div className="mb-4 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-moss">
                <span>Featured</span>
                <span className="h-px w-8 bg-moss/40" />
                <span>{feature.category}</span>
              </div>
              <h2 className="font-serif text-3xl leading-tight text-charcoal lg:text-4xl">
                {feature.title}
              </h2>
              <p className="mt-5 max-w-[52ch] leading-relaxed text-charcoal/70">
                {feature.excerpt}
              </p>
              <p className="mt-6 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-charcoal/70">
                <span>{feature.readMinutes} min read</span>
                <span>·</span>
                <span className="text-moss underline decoration-moss/30 underline-offset-4 transition-colors group-hover:decoration-moss">
                  Read article →
                </span>
              </p>
            </div>
          </Link>
        </section>

        <section className="container-page pb-32">
          <div className="mb-12 flex items-end justify-between gap-8">
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.25em] text-moss">
                All articles
              </p>
              <h2 className="font-serif text-3xl text-charcoal">
                The full archive.
              </h2>
              <div className="mt-6 h-px w-24 bg-moss" />
            </div>
            <p className="hidden text-xs uppercase tracking-[0.2em] text-charcoal/70 md:block">
              {posts.length} entries
            </p>
          </div>

          <ul className="grid grid-cols-1 gap-px border border-charcoal/10 bg-charcoal/10 md:grid-cols-2 lg:grid-cols-3">
            {rest.map((p) => (
              <li key={p.slug} className="bg-cream">
                <Link
                  to="/blog/$slug"
                  params={{ slug: p.slug }}
                  className="group flex h-full flex-col"
                >
                  <figure className="overflow-hidden">
                    <img
                      src={p.image}
                      srcSet={postSrcSet(p)}
                      sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                      alt={p.title}
                      width={1440}
                      height={756}
                      loading="lazy"
                      className="aspect-[16/10] w-full object-cover transition-transform group-hover:scale-[1.03]"
                    />
                  </figure>
                  <div className="flex flex-1 flex-col gap-4 px-6 py-7">
                    <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-moss">
                      <span>{p.category}</span>
                      <span className="h-px w-6 bg-moss/40" />
                      <span className="text-charcoal/70">
                        {p.readMinutes} min
                      </span>
                    </div>
                    <h3 className="font-serif text-xl leading-snug text-charcoal">
                      {p.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-charcoal/70">
                      {p.excerpt}
                    </p>
                    <p className="mt-auto pt-2 text-xs font-medium uppercase tracking-[0.2em] text-moss underline decoration-moss/30 underline-offset-4 transition-colors group-hover:decoration-moss">
                      Read article →
                    </p>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-moss py-24 text-cream">
          <div className="container-page grid items-center gap-10 lg:grid-cols-2">
            <h2 className="font-serif text-4xl leading-tight lg:text-5xl">
              Ready to put compliant disposal into practice?
            </h2>
            <div className="lg:justify-self-end">
              <a
                href="/#enquire"
                className="inline-flex items-center gap-3 rounded-sm bg-cream px-6 py-4 text-sm font-medium text-moss transition-colors hover:bg-cream/90"
              >
                Request a collection →
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}